/**
 * Optional fast path for collection deep-links when the parent already has the
 * same `CarouselData` home Featured Carousel strips use. Reuses the shared
 * `featured-carousel-strip` React Query cache — does not replace the legacy
 * TemplateCollectionCatalogPanel lookup/feed path.
 */
import React, { useMemo } from "react";
import dayjs from "dayjs";
import type { CarouselData, FeaturedCarouselItem, ThemeTokens } from "./types";
import {
  shopContentFormatSlug,
  slugifyShopContentPlatform,
  type ShopCatalogContent,
  type ShopCatalogProduct,
} from "./templateShopCatalog";
import { ShopContentCatalogPanel } from "./templatePages/ShopContentCatalogPanel";
import { ShopProductCatalogPanel } from "./templatePages/ShopProductCatalogPanel";
import { FEATURED_CAROUSEL_POST_LIMIT } from "./featuredCarouselPosts";
import { useFeaturedCarouselStripItems } from "./useFeaturedCarouselStripItems";
import { isRelativeDayWindowDisplay } from "../../../Utils/StayUpToDate/stayUpToDateComponentTabFilters";

export function carouselReadyForHomeStrip(
  carousel: CarouselData | null | undefined,
): carousel is CarouselData {
  if (!carousel) return false;
  const slug = String(carousel.renderSlug || carousel.id || "").trim();
  if (!slug) return false;
  return Boolean(
    String(carousel.componentType || "").trim() ||
      String(carousel.postIds || "").trim() ||
      String(carousel.query || "").trim() ||
      String(carousel.details || "").trim(),
  );
}

function mapFeaturedToCatalogContent(
  item: FeaturedCarouselItem,
): ShopCatalogContent {
  const platform = String(item.platform || "");
  const platformSlug = slugifyShopContentPlatform(platform);
  const type = String(item.postType || "");
  const shopUrl = String(item.shopLink || "").trim();
  const ctaLabel = item.shopCtaLabel || "Watch";
  const shoppable = ctaLabel === "Shop";
  const permalink =
    String(item.permalink || "").trim() || (!shoppable ? shopUrl : "");
  const mediaUrls =
    item.mediaUrls && item.mediaUrls.length > 0
      ? item.mediaUrls
      : item.mediaUrl
        ? [item.mediaUrl]
        : item.image
          ? [item.image]
          : [];
  return {
    postId: String(item.id || "").trim(),
    caption: String(item.title || ""),
    shopUrl,
    shoppable,
    ctaLabel,
    permalink,
    image: String(item.image || ""),
    mediaUrl: String(item.mediaUrl || item.image || ""),
    mediaUrls,
    thumbnailUrls: item.thumbnailUrls || [],
    isVideo: Boolean(item.isVideo),
    isText: Boolean(item.isText),
    isCarousel: mediaUrls.length > 1,
    platform,
    platformSlug,
    type,
    formatSlug: shopContentFormatSlug(type, platformSlug),
    dateLabel: item.postDate ? dayjs(item.postDate).format("MMM D") : "",
  };
}

function mapFeaturedToCatalogProduct(
  item: FeaturedCarouselItem,
): ShopCatalogProduct {
  return {
    postId: String(item.id || "").trim(),
    name: String(item.title || ""),
    url: String(item.shopLink || item.permalink || "").trim(),
    image: String(item.productImage || item.image || ""),
    contentCategory: String(item.brand || ""),
    platform: String(item.platform || ""),
    breadcrumbsRaw: "",
    breadcrumbs: null,
  };
}

type Props = {
  userId: string;
  username: string;
  renderSlug: string;
  T: ThemeTokens;
  pageVariant: "shop" | "content";
  cols: number;
  aspect?: string;
  buttonLabel?: string;
  showButton?: boolean;
  showCategory?: boolean;
  showPrice?: boolean;
  enabled?: boolean;
  carousel: CarouselData;
};

export function TemplateCollectionHomeStripCatalog({
  userId,
  username,
  renderSlug,
  T,
  pageVariant,
  cols,
  aspect = "4/5",
  buttonLabel = "Shop",
  showButton = true,
  showCategory = true,
  showPrice = true,
  enabled = true,
  carousel,
}: Props) {
  const slugTrim = enabled ? String(renderSlug || "").trim() : "";
  const strip = useFeaturedCarouselStripItems({
    enabled: enabled && Boolean(slugTrim),
    userId,
    username,
    carousel,
    limit: FEATURED_CAROUSEL_POST_LIMIT,
    staticItems: [],
    preferStatic: false,
  });

  const filterLc = String(carousel.shoppableContentFilter || "")
    .trim()
    .toLowerCase();
  // LiB day-windows with content/both Show — same posts as the strip, as
  // content cards (not product-bank tiles). Products-only day windows stay
  // on the product masonry.
  const shopAsContentDayWindow =
    pageVariant === "shop" &&
    filterLc !== "products" &&
    isRelativeDayWindowDisplay(
      carousel.componentDisplayName,
      carousel.componentType,
    );

  const contentItems = useMemo(() => {
    if (pageVariant !== "content" && !shopAsContentDayWindow) {
      return [] as ShopCatalogContent[];
    }
    return strip.items
      .map(mapFeaturedToCatalogContent)
      .filter((c) => Boolean(c.postId));
  }, [pageVariant, shopAsContentDayWindow, strip.items]);

  const productItems = useMemo(() => {
    if (pageVariant !== "shop" || shopAsContentDayWindow) {
      return [] as ShopCatalogProduct[];
    }
    return strip.items
      .map(mapFeaturedToCatalogProduct)
      .filter((p) => Boolean(p.postId));
  }, [pageVariant, shopAsContentDayWindow, strip.items]);

  if (pageVariant === "shop" && !shopAsContentDayWindow) {
    return (
      <ShopProductCatalogPanel
        catalogKey={`collection-shop-home-${slugTrim}`}
        T={T}
        products={productItems}
        loading={strip.isLoading && productItems.length === 0}
        userId={userId}
        cols={cols}
        aspect={aspect}
        showButton={showButton}
        showCategory={showCategory}
        showPrice={showPrice}
        buttonLabel={buttonLabel}
        shopView="all"
      />
    );
  }

  return (
    <ShopContentCatalogPanel
      catalogKey={`collection-content-home-${slugTrim}`}
      T={T}
      items={contentItems}
      loading={strip.isLoading && contentItems.length === 0}
      userId={userId}
      username={username}
      cols={cols}
      aspect={aspect}
      buttonLabel={buttonLabel}
    />
  );
}
